import { useCallback, useState } from 'react'
import classNames from 'classnames'
import { Button, LinkButton } from '@/atoms/Button'
import { ExternalLink } from '@/atoms/ExternalLink'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { HeadingSM, TitleLG, LabelLG, AsLabel, CaptionSM } from '@/atoms/Text'
import { BoundedImage } from '@/molecules/BoundedImage/BoundedImage'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate, Translate } from '@/utils/translate/translate-client'

interface FeaturedSectionProps {
  slug?: string
}

export const FeaturedSection: React.FC<FeaturedSectionProps> = ({ slug }) => {
  const [showTrailerModal, setShowTrailerModal] = useState(false)
  const { t } = useTranslate('music')
  const track = useSafeTrack()
  const featuredSectionAssets = [
    {
      slug: 'a-week-away-series',
      modalTrailerUrl: 'https://www.youtube.com/embed/9iMhpAANlmA',
      musicUrl: 'https://lnk.to/AWeekAwaySeries',
      albumArtPath: 'c_fill,w_775,h_775/v1755804221/angel-studios/music/albums/a_week_away',
      albumArtAlt: t('aWeekAwayAlbumArt', 'A Week Away Album Art'),
      title: t('originalMusicAngelOriginalSeries', 'Original Music from the Angel Original Series'),
      description: t(
        'aWeekAwayDescription',
        'At Camp Aweegaway, a Christian summer camp filled with adventure and camaraderie, kids come together for experiences that challenge and inspire them. Between team-building competitions, campfires, and heartfelt conversations, they form deep friendships and uncover new layers of their faith and identity. Each day at camp offers opportunities for growth, connection, and unforgettable memories.',
      ),
      logoPath: '/e_trim/v1755813272/studio-app/catalog/4d54b680-5f85-4167-bdc1-d1cb8f9c5449.webp',
      logoAlt: t('weekAwaySneakPeek', 'A Week Away Sneak Peek'),
      titleSrOnly: t('aWeekAwaySeries', 'A Week Away: The Series'),
    },
    {
      slug: 'david',
      modalTrailerUrl: 'https://www.youtube.com/embed/cyBHli_akQw',
      musicUrl: 'https://lnk.to/DavidSoundtrack',
      albumArtPath:
        'c_fill,w_775,h_775/v1762354972/angel-studios/music/albums/DAV_KEY_AngelStudios_Soundtrack_SQ_3000x3000_052_F1_v3.webp',
      albumArtAlt: t('davidAlbumArt', 'David Soundtrack Album Art'),
      title: t('originalMusicFromAngelOriginalMovie', 'Soundtrack from the Angel Original Film'),
      description: t(
        'davidDescription',
        "From the songs of his mother's heart to the whispers of a faithful God, David's story begins in quiet devotion. When the giant Goliath rises to terrorize a nation, a young shepherd armed with only a sling, a few stones, and unshakable faith steps forward. Pursued by power and driven by purpose, his journey tests the limits of loyalty, love, and courage—culminating in a battle not just for a crown, but for the soul of a kingdom.",
      ),
      logoPath: '/e_trim/v1762209137/studio-app/catalog/04f5ca51-a08c-487c-8b47-6112bc4bb598.webp',
      logoAlt: t('davidLogo', 'David Logo'),
      titleSrOnly: t('davidMovie', 'David: The Movie'),
    },
  ]

  // Find the current asset based on slug, default to first one if slug not found
  const currentAsset = featuredSectionAssets.find((asset) => asset.slug === slug) || featuredSectionAssets[0]!

  const handleTrailerClick = useCallback(() => {
    setShowTrailerModal(true)
    track('Music Page Featured Section Trailer CTA Clicked')
  }, [track])

  const handleModalClick = useCallback(() => {
    setShowTrailerModal(false)
  }, [])

  const handlePreviewClick = useCallback(() => {
    track('Music Page Featured Section Preview CTA Clicked')
  }, [track])

  return (
    <section className={classNames('w-full py-20 core-gray-950')}>
      <div className="px-8 md:mx-auto md:w-[693px] md:px-0 xl:mx-16 xl:w-fit 2xl:mx-auto 2xl:w-full">
        <div className={classNames('flex flex-col gap-[72px] xl:flex-row xl:max-w-[1558px] xl:mx-auto')}>
          <ExternalLink href={currentAsset.musicUrl}>
            <div
              className={classNames(
                'md:max-w-[288px] lg:max-w-[400px] xl:max-w-[510px] xl:basis-1/2 2xl:max-w-[720px] 3xl:max-w-[850px]',
              )}
            >
              <Image
                className="w-full rounded-2xl object-contain"
                src={getCloudinaryImageUrl({ path: currentAsset.albumArtPath })}
                alt={currentAsset.albumArtAlt}
                width={0}
                height={0}
                sizes="100vw"
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
          </ExternalLink>
          <FlexColumn className={classNames('gap-8 xl:my-auto xl:basis-1/2')}>
            <FlexRow className="gap-4">
              <BoundedImage
                className="mb-2 flex items-center justify-center md:justify-start"
                alt={currentAsset.logoAlt}
                path={currentAsset.logoPath}
                width={600}
                height={104}
              />
              <HeadingSM weight="bold" className="sr-only">
                {currentAsset.titleSrOnly}
              </HeadingSM>
            </FlexRow>
            <FlexColumn className="gap-2">
              <TitleLG weight="bold">{currentAsset.title}</TitleLG>
              <CaptionSM className="md:photon-caption-md lg:photon-caption-lg" color="core-gray-400">
                {currentAsset.description}
              </CaptionSM>
            </FlexColumn>
            <div className="flex flex-col gap-2 md:flex-row lg:gap-4">
              <LinkButton
                href={currentAsset.musicUrl}
                variant="white"
                target="_blank"
                onClick={handlePreviewClick}
                className={'btn my-0 mb-4 min-h-[40px] min-w-[142px] rounded-lg py-2 lg:min-h-[56px] lg:w-2/5 lg:px-0'}
              >
                <LabelLG
                  as={AsLabel}
                  color="core-gray-950"
                  weight="semibold"
                  className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                >
                  <PlayIcon color="core-gray-950" size={24} className="mb-0.5 mr-1.5" />
                  <Translate i18nKey="listen" t={t}>
                    Listen
                  </Translate>
                </LabelLG>
              </LinkButton>
              <Button
                variant="core-gray-900"
                className={
                  'btn my-0 mb-4 min-h-[40px] min-w-[142px] rounded-lg py-2 hover:!bg-core-gray-800 lg:min-h-[56px] lg:w-2/5 lg:px-0'
                }
                onClick={handleTrailerClick}
              >
                <LabelLG
                  as={AsLabel}
                  color="white"
                  weight="semibold"
                  className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                >
                  <PlayIcon color="white" size={24} className="mb-0.5 mr-1.5" />
                  <Translate i18nKey="watchTrailer" t={t}>
                    Watch Trailer
                  </Translate>
                </LabelLG>
              </Button>
            </div>
          </FlexColumn>
        </div>
      </div>
      <YoutubeVideoModal
        youTubeUrl={currentAsset.modalTrailerUrl}
        handleClose={handleModalClick}
        showModal={showTrailerModal}
      />
    </section>
  )
}
